import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to Korean
    const savedLanguage = localStorage.getItem('siteLanguage');
    return savedLanguage || 'ko';
  });

  const [isDetectingLocation, setIsDetectingLocation] = useState(false);

  useEffect(() => {
    // Save language preference to localStorage whenever it changes
    localStorage.setItem('siteLanguage', language);
  }, [language]);

  useEffect(() => {
    // Check if we've already detected location for this user
    const hasDetectedLocation = localStorage.getItem('locationDetected');
    
    // If we've already detected location, don't detect again
    if (hasDetectedLocation) {
      return;
    }

    // Detect user location based on IP address
    const detectUserLocation = async () => {
      setIsDetectingLocation(true);
      try {
        // Using ipinfo.io (free, HTTPS-compatible, no API key required)
        const response = await fetch('https://ipinfo.io/json');
        const data = await response.json();
        
        if (data && data.country) {
          // If user is in Korea, set language to Korean, otherwise English
          const detectedLanguage = data.country === 'KR' ? 'ko' : 'en';
          setLanguage(detectedLanguage);
          // Mark that we've detected location so we don't do it again
          localStorage.setItem('locationDetected', 'true');
        }
      } catch (error) {
        console.error('Error detecting user location:', error);
        // On error, keep the current language (which defaults to 'ko')
        // Mark as detected to avoid repeated failed attempts
        localStorage.setItem('locationDetected', 'true');
      } finally {
        setIsDetectingLocation(false);
      }
    };

    detectUserLocation();
  }, []); // Only run once on mount

  const changeLanguage = (lang) => {
    setLanguage(lang);
    // When user manually changes language, mark location as detected
    // so we don't override their choice
    localStorage.setItem('locationDetected', 'true');
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, isDetectingLocation }}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;

